import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ACTION_TYPES, getLogs, exportPdf } from '../../../services/userActivityService';
import { formatLogDetails } from '../../../utils/logDetailsFormat';
import './AuditLogManager.css';

const DETAIL_ANIMATION_MS = 280;
const MENU_ANIMATION_MS = 200;

// Animated expand/collapse detail row: mounts before opening and unmounts
// after closing so the grid-rows transition runs in both directions.
function DetailRow({ open, children }) {
  const [rendered, setRendered] = useState(open);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setRendered(true);
      const raf = requestAnimationFrame(() =>
        requestAnimationFrame(() => setIsOpen(true))
      );
      return () => cancelAnimationFrame(raf);
    }
    setIsOpen(false);
    const timer = setTimeout(() => setRendered(false), DETAIL_ANIMATION_MS);
    return () => clearTimeout(timer);
  }, [open]);

  if (!rendered) return null;

  return (
    <tr className={`row-detail audit-detail-anim${isOpen ? ' open' : ''}`}>
      <td colSpan={5}>
        <div className="audit-detail-grid">
          <div className="audit-detail-inner">{children}</div>
        </div>
      </td>
    </tr>
  );
}

// Same lifecycle for dropdown menus (fade + settle).
function MenuAnim({ open, children }) {
  const [rendered, setRendered] = useState(open);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setRendered(true);
      const raf = requestAnimationFrame(() =>
        requestAnimationFrame(() => setIsOpen(true))
      );
      return () => cancelAnimationFrame(raf);
    }
    setIsOpen(false);
    const timer = setTimeout(() => setRendered(false), MENU_ANIMATION_MS);
    return () => clearTimeout(timer);
  }, [open]);

  if (!rendered) return null;

  return (
    <div className={`audit-menu-anim${isOpen ? ' open' : ''}`}>
      <div className="audit-menu-inner">{children}</div>
    </div>
  );
}

const CATEGORY_ORDER = ['rating', 'report', 'social'];

const UserActivityManager = () => {
  const { t, i18n } = useTranslation();

  // Filters
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedActionTypes, setSelectedActionTypes] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Data & UI state
  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [categoryCounts, setCategoryCounts] = useState(null);
  const [lastPage, setLastPage] = useState(1);

  // Action dropdown
  const [actionDropdownOpen, setActionDropdownOpen] = useState(false);
  const actionDropdownRef = useRef(null);

  // Debounce free-text search so typing doesn't fire a request per keystroke
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (actionDropdownRef.current && !actionDropdownRef.current.contains(e.target)) {
        setActionDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Close dropdown on scroll, but allow scrolling inside the dropdown itself
  useEffect(() => {
    if (!actionDropdownOpen) return;
    const handleScroll = (e) => {
      if (actionDropdownRef.current && actionDropdownRef.current.contains(e.target)) return;
      setActionDropdownOpen(false);
    };
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [actionDropdownOpen]);

  // Fetch logs
  const fetchLogs = useCallback(async (page = 1) => {
    setIsLoading(true);
    setError(null);
    try {
      const filters = {
        page,
        perPage,
        ...(dateFrom ? { dateFrom } : {}),
        ...(dateTo ? { dateTo } : {}),
        ...(selectedActionTypes.length ? { actionType: selectedActionTypes } : {}),
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      };
      const result = await getLogs(filters);
      setLogs(result.logs || []);
      setTotal(result.total || 0);
      setCurrentPage(result.currentPage || 1);
      setLastPage(result.lastPage || 1);
      setCategoryCounts(result.categoryCounts ?? null);
    } catch (err) {
      setError(err?.message || 'Failed to load user activity logs');
      setLogs([]);
    } finally {
      setIsLoading(false);
    }
  }, [dateFrom, dateTo, selectedActionTypes, debouncedSearch, perPage]);

  useEffect(() => {
    setCurrentPage(1);
    fetchLogs(1);
  }, [fetchLogs]);

  const handlePageChange = (page) => {
    if (page < 1 || page > lastPage) return;
    setCurrentPage(page);
    fetchLogs(page);
  };

  const handleClearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setSelectedActionTypes([]);
    setSearchQuery('');
    setPerPage(25);
    setCurrentPage(1);
  };

  const handleActionToggle = (key) => {
    setSelectedActionTypes(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportPdf({
        dateFrom,
        dateTo,
        actionType: selectedActionTypes,
        search: debouncedSearch,
      });
    } catch (err) {
      setError(err?.message || 'Failed to export user activity logs');
      setTimeout(() => setError(null), 4000);
    } finally {
      setExporting(false);
    }
  };

  const hasFilters = dateFrom || dateTo || selectedActionTypes.length || searchQuery;

  const getActionLabel = (actionType) =>
    t(`userActivity.actions.${actionType}`, ACTION_TYPES[actionType]?.label || actionType);

  const getCategory = (log) =>
    log.category || ACTION_TYPES[log.action_type]?.category || 'unknown';

  // Actions grouped by category for the dropdown
  const groupedActionTypes = CATEGORY_ORDER
    .map(category => ({
      category,
      actions: Object.values(ACTION_TYPES).filter(a => a.category === category),
    }))
    .filter(group => group.actions.length > 0);

  const formatDateTime = (iso) => {
    if (!iso) return '—';
    // Normalize fractional seconds to milliseconds (backend sends microseconds)
    const normalized = String(iso).replace(/(\.\d{3})\d+/, '$1');
    const d = new Date(normalized);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleString(i18n.language === 'ar' ? 'ar-SA' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getActionBadgeClass = (category) => {
    switch (category) {
      case 'rating': return 'badge-rating';
      case 'report': return 'badge-report';
      case 'social': return 'badge-social';
      default: return 'badge-default';
    }
  };

  const toggleRow = (id) => {
    setExpandedId(prev => (prev === id ? null : id));
  };

  const startItem = total === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const endItem = Math.min(currentPage * perPage, total);

  return (
    <div className="audit-log-manager">
      {/* Error */}
      {error && (
        <div className="composer-alert alert-error">
          {error}
          <button className="alert-close" onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {/* Filters */}
      <div className="audit-log-filters">
        <div className="filter-group">
          <label>{t('userActivity.dateFrom', 'From')}</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <label>{t('userActivity.dateTo', 'To')}</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </div>

        <div className="filter-group" ref={actionDropdownRef}>
          <label>{t('userActivity.actionType', 'Action')}</label>
          <button
            className="filter-dropdown-trigger"
            onClick={() => setActionDropdownOpen(!actionDropdownOpen)}
          >
            {selectedActionTypes.length === 0
              ? t('userActivity.allActions', 'All actions')
              : selectedActionTypes.length === 1
                ? getActionLabel(selectedActionTypes[0])
                : t('userActivity.selectedCount', '{{count}} selected', { count: selectedActionTypes.length })}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>
          <MenuAnim open={actionDropdownOpen}>
            <div className="filter-dropdown-menu">
              {groupedActionTypes.map(group => (
                <div key={group.category} className="filter-dropdown-group">
                  <div className="filter-dropdown-group-label">
                    <span className="filter-dropdown-group-title">
                      {t(`userActivity.categories.${group.category}`, group.category)}
                      {categoryCounts?.[group.category] > 0 && (
                        <span className="filter-dropdown-group-count">{categoryCounts[group.category]}</span>
                      )}
                    </span>
                  </div>
                  {group.actions.map(action => (
                    <label key={action.key} className="filter-dropdown-item">
                      <input
                        type="checkbox"
                        checked={selectedActionTypes.includes(action.key)}
                        onChange={() => handleActionToggle(action.key)}
                      />
                      <span className={`category-dot ${getActionBadgeClass(action.category)}`} aria-hidden="true"></span>
                      <span className="filter-dropdown-label">{getActionLabel(action.key)}</span>
                    </label>
                  ))}
                </div>
              ))}
            </div>
          </MenuAnim>
        </div>

        <div className="filter-group filter-search">
          <label>{t('userActivity.search', 'Search')}</label>
          <div className="filter-search-input">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input
              type="text"
              placeholder={t('userActivity.searchPlaceholder', 'Search by name, email, or ID...')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="search-clear" onClick={() => setSearchQuery('')}>✕</button>
            )}
          </div>
        </div>

        {hasFilters && (
          <button className="filter-clear-btn" onClick={handleClearFilters}>
            {t('userActivity.clearFilters', 'Clear')}
          </button>
        )}
      </div>

      {/* Stats + export */}
      <div className="audit-log-toolbar">
        <div className="audit-log-stats">
          <div className="stat-box">
            <span className="stat-value">{total}</span>
            <span className="stat-label">{t('userActivity.total', 'Total')}</span>
          </div>
        </div>

        {/* Export button */}
        <div className="audit-log-export-bar">
        <button className="btn-export" onClick={handleExport} disabled={exporting}>
          {exporting ? (
            <span className="loading-spinner-small"></span>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
          )}
          {t('userActivity.export', 'Export')}
        </button>
        </div>
      </div>

      {/* Table */}
      <div className="audit-log-table-wrap">
        {isLoading ? (
          <div className="audit-log-loading">
            <div className="loading-spinner"></div>
            <p>{t('userActivity.loading', 'Loading user activity logs...')}</p>
          </div>
        ) : logs.length === 0 ? (
          <div className="audit-log-empty">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M22 12h-4l-3 9L9 3l-3 9H2"></path>
            </svg>
            <p>{t('userActivity.noLogs', 'No user activity logs found.')}</p>
            {hasFilters && (
              <button className="btn-link" onClick={handleClearFilters}>
                {t('userActivity.clearFilters', 'Clear filters')}
              </button>
            )}
          </div>
        ) : (
          <>
            <table className="audit-log-table">
              <thead>
                <tr>
                  <th>{t('userActivity.dateTime', 'Date & Time')}</th>
                  <th>{t('userActivity.action', 'Action')}</th>
                  <th>{t('userActivity.user', 'User')}</th>
                  <th>{t('userActivity.target', 'Target')}</th>
                  <th>{t('userActivity.whatHappened', 'What Happened')}</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log, idx) => {
                  const rowKey = log.id || idx;
                  const isExpanded = expandedId === rowKey;
                  const category = getCategory(log);
                  const hasDetails = log.details != null &&
                    (typeof log.details === 'string'
                      ? log.details.trim() !== ''
                      : Object.keys(log.details).length > 0);
                  return (
                    <React.Fragment key={rowKey}>
                      <tr
                        className={`row-expandable${isExpanded ? ' row-expanded' : ''}`}
                        onClick={() => toggleRow(rowKey)}
                      >
                        <td className="cell-datetime">
                          <span className="expand-wrap">
                            <svg className={`expand-chevron${isExpanded ? ' open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="9 18 15 12 9 6"></polyline>
                            </svg>
                            {formatDateTime(log.created_at)}
                          </span>
                        </td>
                        <td className="cell-action">
                          <span className={`action-badge ${getActionBadgeClass(category)}`}>
                            {getActionLabel(log.action_type)}
                          </span>
                        </td>
                        <td className="cell-admin">
                          <div className="admin-info">
                            <span className="admin-name">{log.user_name || '—'}</span>
                            {log.user_email && <span className="admin-email">{log.user_email}</span>}
                          </div>
                        </td>
                        <td className="cell-target">
                          {!log.target_id && !log.target_name ? (
                            <span className="target-name">—</span>
                          ) : (
                            <>
                              <span className="target-name">{log.target_name || '—'}</span>
                              {log.target_id && <span className="target-id">#{log.target_id}</span>}
                            </>
                          )}
                        </td>
                        <td className="cell-summary">
                          <span className="summary-text" title={log.summary}>
                            {log.summary || '—'}
                          </span>
                        </td>
                      </tr>
                      <DetailRow open={isExpanded}>
                        <div className="log-detail-panel">
                              <div className="detail-block">
                                <span className="detail-label">{t('userActivity.summary', 'Summary')}</span>
                                <p className="detail-text">{log.summary || '—'}</p>
                              </div>
                              {hasDetails && (
                                <div className="detail-block">
                                  <span className="detail-label">{t('userActivity.details', 'Details')}</span>
                                  {typeof log.details === 'string' ? (
                                    <p className="detail-text">{log.details}</p>
                                  ) : (
                                    <div className="detail-meta-grid">
                                      {formatLogDetails(log.details).map((row) => (
                                        <div className="detail-meta-item" key={row.label}>
                                          <span className="detail-label">{row.label}</span>
                                          <span className="detail-text">{row.value}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                              <div className="detail-meta-grid">
                                <div className="detail-meta-item">
                                  <span className="detail-label">{t('userActivity.category', 'Category')}</span>
                                  <span>
                                    <span className={`action-badge ${getActionBadgeClass(category)}`}>
                                      {t(`userActivity.categories.${category}`, category)}
                                    </span>
                                  </span>
                                </div>
                                <div className="detail-meta-item">
                                  <span className="detail-label">{t('userActivity.actionType', 'Action Type')}</span>
                                  <span className="detail-text">{log.action_type || '—'}</span>
                                </div>
                                <div className="detail-meta-item">
                                  <span className="detail-label">{t('userActivity.ipAddress', 'IP Address')}</span>
                                  <span className="detail-text">{log.ip_address || '—'}</span>
                                </div>
                                <div className="detail-meta-item">
                                  <span className="detail-label">{t('userActivity.timestamp', 'Timestamp')}</span>
                                  <span className="detail-text">{log.created_at ? formatDateTime(log.created_at) : '—'}</span>
                                </div>
                              </div>
                        </div>
                      </DetailRow>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>

            {/* Pagination */}
            <div className="audit-log-pagination-wrap">
              <div className="audit-log-perpage">
                <label>{t('userActivity.perPage', 'Per page')}</label>
                <select value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
              <div className="audit-log-pagination">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage <= 1}
                >
                  {t('userActivity.prev', 'Previous')}
                </button>
                <span>
                  {t('userActivity.showing', 'Showing {{start}}–{{end}} of {{total}}', {
                    start: startItem,
                    end: endItem,
                    total,
                  })}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= lastPage}
                >
                  {t('userActivity.next', 'Next')}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default UserActivityManager;
